/**
 * API クライアント（03 3.4）：openapi-fetch のクライアント 1 つに、共通の fetch（ヘッダ・30 秒の打ち切り・401 の延長と再ログイン・エラーの形）を付ける。
 * 画面は src/api/endpoints/*.ts の関数だけを通して呼ぶ（generated を直接 import しない）。
 *
 * - ベース URL は同じオリジン。Cookie（HttpOnly）で認証し、Authorization ヘッダは付けない（要件書 3.2）
 * - 付けるヘッダは Accept・Content-Type・X-Requested-With（/api・/auth の更新系）・Idempotency-Key（呼び出し側が渡す）だけ（FE-D-028）
 * - 401（error.code が無い＝Authorizer、AUTH_002・AUTH_004）：API-AUTH-03 を 1 回だけ呼び、成功すれば同じヘッダでやり直す。
 *   延長できなければ再ログインダイアログ（ReloginDialog）を出し、成功すれば同じ Idempotency-Key でやり直す（BE D-021）
 * - 403 AUTH_006：ダイアログを出さずにログイン画面へ
 */
import createClient from 'openapi-fetch';
import { FE_MESSAGES, errorText } from '@pcrm/shared';
import type { ListMeta } from '@pcrm/shared';
import type { paths } from './generated/schema';
import { ApiError } from './http-error';

export const REQUEST_TIMEOUT_MS = 30000;

/** 延長・再ログインの仕組み（features/auth の AuthSessionProvider が登録する） */
export interface SessionHandlers {
  /** API-AUTH-03 を呼ぶ（同時に何本来ても 1 回）。延長できたら true */
  refresh(): Promise<boolean>;
  /** 延長できなかったとき。再ログインできたら true（別の利用者なら false） */
  reloginRequired(): Promise<boolean>;
  /** 403 AUTH_006（利用者が無効） */
  accountDisabled(): void;
}

let session_handlers: SessionHandlers | null = null;

export function registerSessionHandlers(handlers: SessionHandlers | null): void {
  session_handlers = handlers;
}

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
/** 401 で延長・再ログインを試さないパス（認証の入口） */
const NO_SESSION_RETRY_PATHS = [
  '/auth/code',
  '/auth/verify',
  '/auth/refresh',
  '/auth/logout',
  '/auth/email-verification',
  '/health',
];

function isSessionRetryTarget(pathname: string): boolean {
  return (pathname.startsWith('/api/') || pathname === '/auth/me') && !NO_SESSION_RETRY_PATHS.includes(pathname);
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  const error = (body as { error?: { code?: string; details?: unknown; trace_id?: string } } | null)?.error;
  const trace_id = error?.trace_id ?? response.headers.get('X-Trace-Id') ?? undefined;
  let code = error?.code;
  if (!code) {
    // Authorizer の 401 は本文が {"message":"Unauthorized"} で error.code が無い → AUTH_004（BE D-021）
    code = response.status === 401 ? 'AUTH_004' : response.status >= 500 ? 'INTERNAL_ERROR' : 'UNKNOWN';
  }
  return new ApiError(response.status, code, error?.details ?? {}, trace_id, errorText(code));
}

function withCommonHeaders(request: Request): Request {
  const headers = new Headers(request.headers);
  const url = new URL(request.url);
  headers.set('Accept', 'application/json');
  // openapi-fetch は本文つきの要求に「application/json」を先に付けるので、それも charset=utf-8 付きに置き換える
  // （243 UT-725）。ほかの Content-Type（呼び出し側が決めたもの）はそのまま
  const content_type = headers.get('Content-Type');
  if (
    request.method !== 'GET' &&
    request.method !== 'HEAD' &&
    (content_type === null || content_type.trim().toLowerCase() === 'application/json')
  ) {
    headers.set('Content-Type', 'application/json; charset=utf-8');
  }
  if (MUTATION_METHODS.has(request.method) && (url.pathname.startsWith('/api/') || url.pathname.startsWith('/auth/'))) {
    headers.set('X-Requested-With', 'XMLHttpRequest');
  }
  headers.delete('Authorization');
  return new Request(request, { headers, credentials: 'same-origin' });
}

/** 30 秒で打ち切る（FE-D-032 の S3 送信は別の部品） */
async function sendOnce(request: Request): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const caller_signal = request.signal;
  const onCallerAbort = () => controller.abort();
  caller_signal.addEventListener('abort', onCallerAbort);
  try {
    return await fetch(new Request(request, { signal: controller.signal }));
  } catch (error) {
    if (controller.signal.aborted && !caller_signal.aborted) {
      throw new ApiError(0, 'TIMEOUT', {}, undefined, FE_MESSAGES['FE-MSG-E-001'].text);
    }
    if (caller_signal.aborted) {
      throw error;
    }
    throw new ApiError(0, 'NETWORK', {}, undefined, FE_MESSAGES['FE-MSG-E-002'].text);
  } finally {
    clearTimeout(timer);
    caller_signal.removeEventListener('abort', onCallerAbort);
  }
}

/** openapi-fetch に渡す fetch。失敗は ApiError を投げる（成功の Response だけを返す） */
export async function pcrmFetch(input: Request): Promise<Response> {
  const prepared = withCommonHeaders(input);
  const retry_copy = prepared.clone();
  const response = await sendOnce(prepared);
  if (response.ok) {
    return response;
  }
  const error = await toApiError(response);
  const pathname = new URL(prepared.url).pathname;
  if (response.status === 401 && isSessionRetryTarget(pathname) && session_handlers) {
    if (await session_handlers.refresh()) {
      return retryOnce(retry_copy);
    }
    if (await session_handlers.reloginRequired()) {
      return retryOnce(retry_copy);
    }
    throw error;
  }
  if (response.status === 403 && error.code === 'AUTH_006') {
    session_handlers?.accountDisabled();
  }
  throw error;
}

async function retryOnce(request: Request): Promise<Response> {
  const response = await sendOnce(request);
  if (!response.ok) {
    throw await toApiError(response);
  }
  return response;
}

export const api_client = createClient<paths>({
  baseUrl: typeof window === 'undefined' ? 'http://localhost' : window.location.origin,
  fetch: pcrmFetch,
});

/** openapi-fetch の結果から data を取り出す（エラーは pcrmFetch が ApiError で投げる） */
export function unwrapData<T>(result: { data?: { data: T } }): T {
  if (!result.data) {
    throw new ApiError(500, 'INTERNAL_ERROR', {}, undefined, errorText('INTERNAL_ERROR'));
  }
  return result.data.data;
}

/**
 * 1 件を 1 件の一覧（{ data: [item], meta }）で返す API の中身を取り出す（243 N-11：API-CHKL-12・CSV-04・CSV-05・FORM-08）。
 * 要素が無いときは 500 と同じ扱い
 */
export function unwrapSingleItemList<T>(result: { data?: { data: T[] } }): T {
  const item = result.data?.data[0];
  if (item === undefined) {
    throw new ApiError(500, 'INTERNAL_ERROR', {}, undefined, errorText('INTERNAL_ERROR'));
  }
  return item;
}

/** 一覧の応答（{ data, meta }：02 2.4）を取り出す */
export function unwrapList<T>(result: { data?: { data: T[]; meta: ListMeta } }): { data: T[]; meta: ListMeta } {
  if (!result.data) {
    throw new ApiError(500, 'INTERNAL_ERROR', {}, undefined, errorText('INTERNAL_ERROR'));
  }
  return { data: result.data.data, meta: result.data.meta };
}

/** Idempotency-Key を付ける（02 2.8。任意の API でも同じ操作の押し直しに同じ値を使う：03 3.4.4） */
export function idempotencyHeaders(idempotency_key: string | undefined): Record<string, string> {
  return idempotency_key ? { 'Idempotency-Key': idempotency_key } : {};
}
